/**
 * When each freshness badge's source next refreshes, for the "Next sync" /
 * "Next update" line in its tooltip. All times UTC.
 * ponytail: mirrors the cron schedules in supabase/migrations (sync-keitaro
 * 20260929140000, refresh-clicks-daily 20261008120000, refresh_analytics_hourly,
 * n8n GSC 03:00 + rollup 04:00) - change both together.
 */
export const PIPELINE_SCHEDULE = {
  keitaro_sync: { label: "Next sync", minutes: [1, 16, 31, 46] },
  // recompute starts at :00 and takes 1-2 min
  clicks_data: { label: "Next update", minutes: [2] },
  analytics_data: { label: "Next update", minutes: [46], hours: [0, 4, 8, 12, 16, 20] },
  // n8n pulls at 03:00, the page reads the rollup rebuilt at 04:00
  gsc: { label: "Next sync", minutes: [0], hours: [4] },
} satisfies Record<string, { label: string; minutes: number[]; hours?: number[] }>;

export type PipelineSource = keyof typeof PIPELINE_SCHEDULE;

/** First whole UTC minute after `now` that matches the schedule. */
export function nextRunUtc(now: Date, minutes: number[], hours?: number[]): Date {
  const t = new Date(now);
  t.setUTCSeconds(0, 0);
  for (let i = 0; i < 48 * 60; i++) {
    t.setUTCMinutes(t.getUTCMinutes() + 1);
    if (minutes.includes(t.getUTCMinutes()) && (!hours || hours.includes(t.getUTCHours()))) break;
  }
  return t;
}

/** "Next sync ~14:16 UTC" */
export function nextRunLine(source: PipelineSource, now: Date = new Date()): string {
  const s: { label: string; minutes: number[]; hours?: number[] } = PIPELINE_SCHEDULE[source];
  return `${s.label} ~${nextRunUtc(now, s.minutes, s.hours).toISOString().slice(11, 16)} UTC`;
}

/** GSC's newest day may be this many days behind today (UTC) and still be healthy. */
export const GSC_STALE_DAYS = 4;

/**
 * Tone of the GSC Sync badge from the newest day GSC has data for. Healthy is
 * today-3 after the 04:00 UTC rollup and today-4 before it (Google lags ~48 h,
 * n8n pulls at 03:00), so only a day older than today-4 means the sync stopped.
 * Compared as UTC dates: "older than 4 x 24 h" from a midnight date turned the
 * badge yellow every night 00:00-04:00 UTC on a healthy pipeline.
 */
export function gscFreshnessTone(
  latest: string | null | undefined,
  nowMs: number,
): "positive" | "warning" | null {
  if (!latest) return null;
  const oldestHealthy = new Date(nowMs - GSC_STALE_DAYS * 86_400_000).toISOString().slice(0, 10);
  return latest.slice(0, 10) < oldestHealthy ? "warning" : "positive";
}
